import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Crown,
  Plus,
  Eye,
  Users,
} from "lucide-react";
import StatsCard from "./components/stats-card";
import { useNavigate } from "react-router-dom";
import "./dashboard.css";
import Navbar from "../../../components/navbar";
import { useAuth } from "../../../common/store/authStore";
import { useTournaments } from "../../../common/store/tournamentStore";
import TournamentCard from "../tournaments/components/tournament-card";

function Dashboard() {
  useEffect(() => {
    document.title = "Dashboard";
  }, []);

  const navigate = useNavigate();

  const { authenticatedUser } = useAuth();
  const isLoggedIn = !!authenticatedUser;

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/login");
    }
  }, [isLoggedIn, navigate]);

  const { hostedTournaments, joinedTournaments, loading } = useTournaments();

  const joinedStats = useMemo(() => {
    const total = joinedTournaments.length;
    const upcoming = joinedTournaments.filter(
      (t) => t.status === "upcoming",
    ).length;
    const completed = joinedTournaments.filter(
      (t) => t.status === "completed",
    ).length;
    return { total, upcoming, completed };
  }, [joinedTournaments]);

  const hostedStats = useMemo(() => {
    const total = hostedTournaments.length;
    const upcoming = hostedTournaments.filter(
      (t) => t.status === "upcoming",
    ).length;
    const completed = hostedTournaments.filter(
      (t) => t.status === "completed",
    ).length;
    return { total, upcoming, completed };
  }, [hostedTournaments]);

  const [activeTab, setActiveTab] = useState("joined");

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading your tournament dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-body">
      <Navbar />
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div className="header-content">
            <h1>Tournament Dashboard</h1>
            <p>Manage your tournaments and track your gaming journey</p>
          </div>
          <div className="header-actions">
            <Link to="/tournaments" className="header-btn">
              <Eye />
              Browse Tournaments
            </Link>
            <Link to="/create-tournament" className="header-btn primary">
              <Plus />
              Create Tournament
            </Link>
            <Link to="/profile" className="header-btn">
              <Users />
              Profile
            </Link>
          </div>
        </div>

        <div className="stats-overview">
          <StatsCard
            title="Joined Tournaments"
            stats={joinedStats}
            icon={<Users />}
            color="#007bff"
            loading={loading}
          />
          <StatsCard
            title="Hosted Tournaments"
            stats={hostedStats}
            icon={<Crown />}
            color="#ffc107"
            loading={loading}
          />
          {/* <StatsCard
            title="Live Fixtures"
            stats={fixturesStats}
            icon={<TrendingUp />}
            color="#4ECDC4"
          /> */}
        </div>

        <div className="tab-navigation">
          <button
            className={`tab-btn ${activeTab === "joined" ? "active" : ""}`}
            onClick={() => setActiveTab("joined")}
          >
            <Users />
            Joined Tournaments ({joinedTournaments.length})
          </button>
          <button
            className={`tab-btn ${activeTab === "hosted" ? "active" : ""}`}
            onClick={() => setActiveTab("hosted")}
          >
            <Crown />
            Hosted Tournaments ({hostedTournaments.length})
          </button>
          {/* <button
            className={`tab-btn ${activeTab === "fixtures" ? "active" : ""}`}
            onClick={() => setActiveTab("fixtures")}
          >
            <FaChartLine />
            Live Fixtures ({hostedFixtures.length})
          </button> */}
        </div>

        <div className="dashboard-content">
          {activeTab === "joined" && (
            <div className="tournaments-section">
              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading joined tournaments...</p>
                </div>
              ) : joinedTournaments.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <Users />
                  </div>
                  <h3>No Joined Tournaments</h3>
                  <p>
                    You haven't joined any tournaments yet. Start your gaming
                    journey!
                  </p>
                  <Link to="/tournaments" className="empty-state-btn">
                    <Eye />
                    Browse Tournaments
                  </Link>
                </div>
              ) : (
                <div className="tournaments-grid">
                  {joinedTournaments.map((tournament) => (
                    <TournamentCard
                      key={tournament._id}
                      tournament={tournament}
                      type="joined"
                      // onAddToCalendar={addToCalendar}
                      // onShare={shareTournament}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "hosted" && (
            <div className="tournaments-section">
              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading hosted tournaments...</p>
                </div>
              ) : hostedTournaments.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <Crown />
                  </div>
                  <h3>No Hosted Tournaments</h3>
                  <p>You haven't created any tournaments yet. Start hosting!</p>
                  <Link to="/create-tournament" className="empty-state-btn">
                    <Plus />
                    Create Tournament
                  </Link>
                </div>
              ) : (
                <div className="tournaments-grid">
                  {hostedTournaments.map((tournament) => (
                    <TournamentCard
                      tournament={tournament}
                      type="hosted"
                      // onAddToCalendar={addToCalendar}
                      // onShare={shareTournament}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* {activeTab === "fixtures" && (
            <div className="tournaments-section">
              {isLoadingFixtures ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading live fixtures...</p>
                </div>
              ) : hostedFixtures.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <FaChartLine />
                  </div>
                  <h3>No Live Fixtures</h3>
                  <p>
                    You haven't created any live fixtures yet. Start hosting!
                  </p>
                  <Link to="/create-bracket" className="empty-state-btn">
                    <FaPlus />
                    Create Fixture
                  </Link>
                </div>
              ) : (
                <div className="tournaments-grid">
                  {hostedFixtures.map((fixture, idx) => (
                    <div className="tournament-card" key={idx}>
                      <div className="card-header">
                        <div className="tournament-image">
                          <img
                            src={
                              fixture.tournamentImage || fixture.gameImageUrl
                            }
                            alt={fixture.selectedGame}
                          />
                          <div className="image-overlay"></div>
                        </div>
                        <div
                          className="status-badge"
                          style={{ backgroundColor: "#FFB400" }}
                        >
                          <FaClock className="status-icon" size={12} />
                          <span>FIXTURE</span>
                        </div>
                        <div className="game-badge">
                          <img
                            src={fixture.gameImageUrl}
                            alt={fixture.selectedGame}
                          />
                        </div>
                      </div>
                      <div className="card-body">
                        <div className="tournament-info">
                          <h3 className="tournament-name">
                            {fixture.tournamentURL}
                          </h3>
                          <div className="host-info">
                            <FaUsers className="host-icon" size={16} />
                            <span className="host-name">
                              {fixture.hostUsername}
                            </span>
                          </div>
                        </div>
                        <div className="tournament-stats">
                          <div className="stat-item">
                            <FaGamepad className="stat-icon" size={16} />
                            <span className="stat-label">Game</span>
                            <span className="stat-value">
                              {fixture.selectedGame}
                            </span>
                          </div>
                          <div className="stat-item">
                            <FaTrophy className="stat-icon" size={16} />
                            <span className="stat-label">Tournament Type</span>
                            <span className="stat-value">
                              {fixture.tournamentType || "N/A"}
                            </span>
                          </div>
                          <div className="stat-item">
                            <FaCalendarAlt className="stat-icon" size={16} />
                            <span className="stat-label">Created</span>
                            <span className="stat-value">
                              {fixture.tournamentCreationDate}
                            </span>
                          </div>
                        </div>
                        <div className="prize-section">
                          <div className="prize-info">
                            <div className="prize-item">
                              <FaUsers className="prize-icon" size={16} />
                              <div className="prize-details">
                                <span className="prize-label">
                                  Participants
                                </span>
                                <span className="prize-amount">
                                  {fixture.participants
                                    ? Array.isArray(fixture.participants)
                                      ? fixture.participants.length
                                      : Object.keys(fixture.participants).length
                                    : 0}
                                </span>
                              </div>
                            </div>
                            <div className="prize-item">
                              <FaTrophy className="prize-icon" size={16} />
                              <div className="prize-details">
                                <span className="prize-label">Rounds</span>
                                {fixture.rounds
                                  ? Object.keys(fixture.rounds).length +
                                    " rounds"
                                  : "N/A"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="card-footer">
                        <Link
                          className="view-tournament-btn"
                          to={`/fixtures/${fixture.tournamentURL}`}
                        >
                          <span>View Fixtures</span>
                          <FaStar className="btn-icon" size={16} />
                        </Link>
                      </div>
                      <div className="card-glow"></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
